import { useState, useCallback, useRef } from 'react';

export interface FilterState {
  people?: string;
  topFive?: string;
}

export interface UseDashboardFiltersReturn {
  filters: FilterState;
  handleFilterChange: (filterType: keyof FilterState, value?: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  filtersKey: number;
}

export const useDashboardFilters = (
  onFiltersChange?: (filters: FilterState) => void
): UseDashboardFiltersReturn => {
  const [filters, setFilters] = useState<FilterState>({});
  const keyRef = useRef(0);

  const handleFilterChange = useCallback(
    (filterType: keyof FilterState, value?: string) => {
      setFilters(currentFilters => {
        const newFilters = { ...currentFilters };
        if (value) {
          newFilters[filterType] = value;
        } else {
          delete newFilters[filterType];
        }

        onFiltersChange?.(newFilters);
        return newFilters;
      });
    },
    [onFiltersChange]
  );

  const clearFilters = useCallback(() => {
    keyRef.current += 1; // Force re-render of SelectButton components
    const emptyFilters = {};
    setFilters(emptyFilters);
    onFiltersChange?.(emptyFilters);
  }, [onFiltersChange]);

  const hasActiveFilters = Object.values(filters).some(
    value => value !== undefined
  );

  return {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters,
    filtersKey: keyRef.current,
  };
};
